import React, { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { addKiosk } from '/api/Kiosk';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useKiosks } from 'lib/hooks/queries/kiosks/useKiosks';
import { useMember } from 'lib/hooks/queries/member/useMember';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Button/Button';
import Modal from 'components/shared/Modal/Modal';

import NewKiosk from './NewKiosk';
import { DarkColors } from './lib/darkColors';

export default function NewKioskModal({ isOpen, handleClose }) {
  const outletContext = useOutletContext();

  const { data: kioskData } = useKiosks({ params: { practiceId: outletContext?.practiceId } });
  const [status, setStatus] = useState('pending');
  const [confirmedKiosk, setConfirmedKiosk] = useState(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    status: Yup.string().required('Status is required'),
    color: Yup.string().required('Color is required')
  });

  const { data } = useMember({});

  const getColor = useMemo(() => {
    if (kioskData?.kiosks?.length > 0) {
      const colors = kioskData?.kiosks.map((k) => k.color);
      const newArray = DarkColors.filter((item) => !colors.includes(item));
      const randomValue = newArray[Math.floor(Math.random() * newArray.length)];
      return randomValue;
    }
    return '#13B9FF';
  }, [kioskData?.kiosks]);

  const formik = useFormik({
    initialValues: {
      name: `Kiosk ${kioskData?.kiosks?.length || 0 + 1}`,
      color: getColor,
      status: '',
      description: '',
      notify_users: []
    },
    enableReinitialize: true,
    validationSchema
  });

  const addNewKiosk = async () => {
    const addedKiosk = await addKiosk(navigate, {
      practiceId: outletContext?.practiceId,
      values: {
        ...formik.values,
        notify_users: formik?.values?.notify_users.map((np) => np.value)
      },
      kioskId: confirmedKiosk?.id
    });
    setStatus('pending');
    handleClose();
    if (addedKiosk.code == 0) {
      showAlert({ title: 'Kiosk added successfully', color: 'success' });
    } else {
      showAlert({ title: 'There was a problem adding Kiosk!', color: 'danger' });
    }
    formik.resetForm();
    queryClient.invalidateQueries(['kiosks']);
  };

  return (
    <Modal
      isOpen={isOpen}
      title="New kiosk"
      handleClose={handleClose}
      footer={
        <div className="flex w-full justify-between">
          <Button text="Cancel" onClick={handleClose} variant="tertiary" size="small" />
          <Button
            text="Add new kiosk"
            onClick={addNewKiosk}
            isDisabled={status != 'success' || !formik.isValid}
            color="primary"
            size="small"
          />
        </div>
      }>
      <NewKiosk
        setConfirmedKiosk={setConfirmedKiosk}
        status={status}
        practitioners={data?.members?.map(({ user_id, f_name, l_name }) => ({
          value: user_id,
          label: `${f_name} ${l_name}`
        }))}
        setStatus={setStatus}
        formik={formik}
        kiosks={kioskData?.kiosks}
      />
    </Modal>
  );
}
